import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { formatDateTime } from '../utils/dateFormat';
import { FileUp, Calendar, FileText, X, Clock, Link, Mail, Edit } from 'lucide-react';
import DocumentUpload from '../components/DocumentUpload';
import {
  fadeInUp,
  staggerContainer,
  fastStaggerContainer,
  scaleIn,
} from '../utils/animations';

interface Profile {
  id: string;
  role: string;
  prenume: string;
  nume: string;
  email: string;
  consultation_status:
    | 'not_requested'
    | 'requested'
    | 'planned'
    | 'closed'
    | 'paid'
    | 'contacted';
  planned_date?: string;
  meeting_url?: string;
  cnp?: string;
  serviciu?: 'standard' | 'asociatie' | 'probono';
  telefon?: string;
  drive_link?: string;
  created_at?: string;
  notite?: string;
}

interface FileObject {
  name: string;
  id: string;
  created_at: string;
}

interface PaymentNotification {
  message: string;
  type: 'success' | 'error';
}

  export default function Doctor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [futurePlannedPatients, setFuturePlannedPatients] = useState<Profile[]>(
    []
  );
  const [pastPlannedPatients, setPastPlannedPatients] = useState<Profile[]>([]);
  const [pendingPatients, setPendingPatients] = useState<Profile[]>([]);
  const [doctorName, setDoctorName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [showPatientsPopup, setShowPatientsPopup] = useState(false);
  const [showClosedPopup, setShowClosedPopup] = useState(false);
  const [unregisteredPatients, setUnregisteredPatients] = useState<Profile[]>(
    []
  );
  const [closedConsultations, setClosedConsultations] = useState<Profile[]>([]);
  const [showUpload, setShowUpload] = useState(false);
  const [patient, setPatient] = useState<Profile | null>(null);
  const [paymentNotification, setPaymentNotification] =
    useState<PaymentNotification | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [closedSearchQuery, setClosedSearchQuery] = useState('');
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedPatientForNotes, setSelectedPatientForNotes] = useState<Profile | null>(null);
  const [currentNote, setCurrentNote] = useState('');

  const togglePatientStatus = async (
    patientId: string,
    currentStatus: string
  ) => {
    try {
      const newStatus =
        currentStatus === 'not_requested' ? 'contacted' : 'not_requested';

      const { error } = await supabase
        .from('profiles')
        .update({ consultation_status: newStatus })
        .eq('id', patientId);

      if (error) throw error;

      // Update local state to reflect the change
      setUnregisteredPatients((prevPatients) =>
        prevPatients.map((patient) =>
          patient.id === patientId
            ? { ...patient, consultation_status: newStatus }
            : patient
        )
      );
    } catch (error) {
      console.error('Error toggling patient status:', error);
      setPaymentNotification({
        message: 'Eroare la actualizarea statusului pacientului',
        type: 'error',
      });
      setTimeout(() => setPaymentNotification(null), 3000);
    }
  };

  const saveNotes = async (patientId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ notite: notes })
        .eq('id', patientId);

      if (error) throw error;

      // Update local state for unregistered patients
      setUnregisteredPatients((prev) =>
        prev.map((p) =>
          p.id === patientId ? { ...p, notite: notes } : p
        )
      );
      // Update local state for closed consultations
      setClosedConsultations((prev) =>
        prev.map((p) =>
          p.id === patientId ? { ...p, notite: notes } : p
        )
      );

      setPaymentNotification({
        message: 'Notițe salvate cu succes',
        type: 'success',
      });
      setTimeout(() => setPaymentNotification(null), 3000);
      setShowNotesModal(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      setPaymentNotification({
        message: 'Eroare la salvarea notițelor',
        type: 'error',
      });
      setTimeout(() => setPaymentNotification(null), 3000);
    }
  };

  const openNotesModal = (patient: Profile) => {
    setSelectedPatientForNotes(patient);
    setCurrentNote(patient.notite || '');
    setShowNotesModal(true);
  };

  // Helper function to get patient styling based on serviciu
  const getPatientStyles = (serviciu?: string) => {
    if (serviciu === 'standard') {
      return 'bg-green-50 border-l-4 border-green-500';
    } else if (serviciu === 'probono') {
      return 'bg-purple-50 border-l-4 border-purple-500';
    } else {
      return 'border rounded-lg'; // Default styling
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) throw authError;
      if (!user) {
        navigate('/login');
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, prenume, nume')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile || profile.role !== 'doctor') {
        navigate('/');
        return;
      }

      setDoctorName(`${profile.prenume} ${profile.nume}`);

      // Get current date for comparison
      const now = new Date();

      // Fetch planned patients
      const { data: planned, error: plannedError } = await supabase
        .from('profiles')
        .select(
          'id, role, prenume, nume, email, consultation_status, planned_date, meeting_url, serviciu'
        )
        .eq('role', 'pacient')
        .eq('consultation_status', 'planned')
        .order('planned_date', { ascending: true });

      if (plannedError) throw plannedError;

      // Split planned patients into future and past
      const future: Profile[] = [];
      const past: Profile[] = [];

      planned?.forEach((patient) => {
        if (patient.planned_date) {
          const plannedDate = new Date(patient.planned_date);
          if (plannedDate > now) {
            future.push(patient);
          } else {
            past.push(patient);
          }
        }
      });

      setFuturePlannedPatients(future);
      setPastPlannedPatients(past);

      // Fetch pending patients
      const { data: pending, error: pendingError } = await supabase
        .from('profiles')
        .select('id, role, prenume, nume, email, cnp, consultation_status, serviciu')
        .eq('role', 'pacient')
        .eq('consultation_status', 'requested');

      if (pendingError) throw pendingError;
      setPendingPatients(pending || []);

      // Fetch unregistered patients
      const { data: unregistered, error: unregisteredError } = await supabase
        .from('profiles')
        .select(
          'id, role, prenume, nume, email, cnp, telefon, consultation_status, created_at, notite'
        )
        .eq('role', 'pacient')
        .in('consultation_status', ['not_requested', 'paid', 'contacted'])
        .order('created_at', { ascending: false });

      if (unregisteredError) throw unregisteredError;
      setUnregisteredPatients(unregistered || []);

      // Fetch closed consultations
      const { data: closed, error: closedError } = await supabase
        .from('profiles')
        .select(
          'id, role, prenume, nume, email, cnp, consultation_status, planned_date, notite'
        )
        .eq('role', 'pacient')
        .eq('consultation_status', 'closed')
        .order('planned_date', { ascending: false });

      if (closedError) throw closedError;
      setClosedConsultations(closed || []);
    } catch (error) {
      console.error('Error in checkUser:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  if (error) {
    return <div className="p-8 text-center text-red-600">Error: {error}</div>;
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-16 overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.6, 0],
              transition: {
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-8">
        <motion.div
          className="bg-white rounded-lg shadow p-6"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.div className="mb-8" variants={fadeInUp}>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Bun venit, Dr. {doctorName}
            </h1>
            <p className="text-gray-600">
              Aici puteți gestiona consultațiile și documentele pacienților
              dumneavoastră.
            </p>
          </motion.div>

          <motion.div
            className="mb-4 p-4 bg-gray-100 rounded-lg"
            variants={fadeInUp}
          >
            <p>
              Pacienți programați pentru comisie:{' '}
              {futurePlannedPatients.length + pastPlannedPatients.length}
            </p>
            <p>
              Pacienți care așteaptă să fie programați: {pendingPatients.length}
            </p>
            <p
              className="cursor-pointer hover:text-[#007FFF] transition-colors"
              onClick={() => setShowPatientsPopup(true)}
            >
              Pacienți înregistrați fără cereri: {unregisteredPatients.length}
            </p>
            <p
              className="cursor-pointer hover:text-[#007FFF] transition-colors"
              onClick={() => setShowClosedPopup(true)}
            >
              Pacienți cu comisii încheiate: {closedConsultations.length}
            </p>
          </motion.div>

          <motion.div
            className="space-y-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer}
          >
            {/* Past Consultations Section */}
            {pastPlannedPatients.length > 0 && (
              <motion.section variants={fadeInUp}>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-amber-600">
                  <Clock className="w-5 h-5" />
                  Consultații efectuate - Așteptare generare scrisoare
                </h2>
                <div className="space-y-4">
                  {pastPlannedPatients.map((patient) => (
                    <motion.div
                      key={patient.id}
                      className="border-l-4 border-amber-500 bg-amber-50 rounded-lg p-4"
                      whileHover={{ x: 4, transition: { duration: 0.2 } }}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-medium">
                            {patient.prenume} {patient.nume}
                          </h3>
                          {patient.planned_date && (
                            <p className="text-sm text-gray-600">
                              {formatDateTime(patient.planned_date)}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate('/doctor/medical-letters')}
                            className="flex items-center gap-1 px-3 py-1 bg-amber-100 hover:bg-amber-200 rounded-md text-sm text-amber-700"
                          >
                            <FileText className="w-4 h-4" />
                            Vezi scrisoarea medicală
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.section>
            )}

            {/* Future Consultations Section */}
            <motion.section variants={fadeInUp}>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Consultații Programate
              </h2>
              <div className="space-y-4">
                {futurePlannedPatients.map((patient) => (
                  <motion.div
                    key={patient.id}
                    className={`p-4 ${getPatientStyles(patient.serviciu)}`}
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">
                          {patient.prenume} {patient.nume}
                        </h3>
                        {patient.planned_date && (
                          <p className="text-sm text-gray-600">
                            {formatDateTime(patient.planned_date)}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {patient.meeting_url && (
                          <a
                            href={patient.meeting_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm"
                          >
                            Participă la Consultație
                          </a>
                        )}
                        <button
                          onClick={() =>
                            navigate(`/doctor/patient/${patient.id}/documents`)
                          }
                          className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-md text-sm"
                        >
                          <FileText className="w-4 h-4" />
                          Dosar pacient
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {futurePlannedPatients.length === 0 && (
                  <p className="text-gray-500">
                    Nu există consultații programate
                  </p>
                )}
              </div>
            </motion.section>

            {/* Pending Requests */}
            <motion.section variants={fadeInUp}>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Cereri în Așteptare
              </h2>
              <div className="space-y-4">
                {pendingPatients.map((patient) => (
                  <motion.div
                    key={patient.id}
                    className={`p-4 ${getPatientStyles(patient.serviciu)}`}
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium">
                          {patient.prenume} {patient.nume}
                        </h3>
                        <p className="text-sm text-gray-600">{patient.email}</p>
                        <p className="text-sm text-gray-600">
                          CNP: {patient.cnp}
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          navigate(`/doctor/patient/${patient.id}/documents`)
                        }
                        className="flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-md text-sm"
                      >
                        <FileText className="w-4 h-4" />
                        Dosar pacient
                      </button>
                    </div>
                  </motion.div>
                ))}
                {pendingPatients.length === 0 && (
                  <p className="text-gray-500">Nu există cereri în așteptare</p>
                )}
              </div>
            </motion.section>
          </motion.div>
        </motion.div>
      </div>

      {/* Unregistered Patients Popup */}
      <AnimatePresence>
        {showPatientsPopup && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg shadow-xl w-[95vw] max-w-full my-4 flex flex-col max-h-[90vh] min-w-[300px] mx-auto px-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Pacienți înregistrați fără cereri
                  </h3>
                  <button
                    onClick={() => setShowPatientsPopup(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Caută în pacienți..."
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-normal whitespace-normal min-w-[160px]">
                            Nume complet
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-normal whitespace-normal min-w-[180px]">
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-normal whitespace-normal min-w-[120px]">
                            Telefon
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-normal whitespace-normal min-w-[140px]">
                            CNP
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-normal whitespace-normal min-w-[150px]">
                            Cont Creat
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-normal whitespace-normal min-w-[140px]">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-normal whitespace-normal min-w-[120px]">
                            Notițe
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-normal whitespace-normal min-w-[120px]">
                            Acțiuni
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {unregisteredPatients
                          .filter(
                            (patient) =>
                              `${patient.prenume} ${patient.nume}`
                                .toLowerCase()
                                .includes(searchQuery.toLowerCase()) ||
                              patient.email
                                ?.toLowerCase()
                                .includes(searchQuery.toLowerCase()) ||
                              patient.telefon?.includes(searchQuery) ||
                              patient.cnp?.includes(searchQuery) ||
                              patient.consultation_status
                                ?.toLowerCase()
                                .includes(searchQuery.toLowerCase())
                          )
                          .map((patient) => (
                            <tr
                              key={patient.id}
                              className={
                                patient.consultation_status === 'paid'
                                  ? 'bg-green-50'
                                  : patient.consultation_status === 'contacted'
                                  ? 'bg-yellow-50'
                                  : ''
                              }
                            >
                              <td className="px-4 py-4 whitespace-normal text-sm text-gray-900">
                                {patient.prenume} {patient.nume}
                              </td>
                              <td className="px-4 py-4 whitespace-normal text-sm text-gray-900">
                                {patient.email}
                              </td>
                              <td className="px-4 py-4 whitespace-normal text-sm text-gray-900">
                                {patient.telefon || '-'}
                              </td>
                              <td className="px-4 py-4 whitespace-normal text-sm text-gray-900">
                                {patient.cnp}
                              </td>
                              <td className="px-4 py-4 whitespace-normal text-sm text-gray-900">
                                {patient.created_at
                                  ? formatDateTime(patient.created_at)
                                  : '-'}
                              </td>
                              <td className="px-4 py-4 whitespace-normal text-sm">
                                {patient.consultation_status ===
                                  'not_requested' ||
                                patient.consultation_status === 'contacted' ? (
                                  <button
                                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer ${
                                      patient.consultation_status ===
                                      'contacted'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                    onClick={() =>
                                      togglePatientStatus(
                                        patient.id,
                                        patient.consultation_status
                                      )
                                    }
                                  >
                                    {patient.consultation_status === 'contacted'
                                      ? 'Contactat'
                                      : 'Nu a solicitat programare'}
                                  </button>
                                ) : (
                                  <span
                                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800`}
                                  >
                                    Plătit
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-4 whitespace-normal text-sm text-gray-900">
                                <button
                                  onClick={() => openNotesModal(patient)}
                                  className={`flex items-center gap-1 px-3 py-1 rounded-md transition-colors ${
                                    patient.notite && patient.notite.trim() !== ''
                                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  <Edit className="w-4 h-4" />
                                  Notițe
                                </button>
                              </td>
                              <td className="px-4 py-4 whitespace-normal text-sm text-gray-900">
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/doctor/patient/${patient.id}/documents`
                                    )
                                  }
                                  className="text-[#007FFF] hover:text-[#024994] transition-colors"
                                >
                                  Vezi documentele
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 flex-shrink-0">
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowPatientsPopup(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Închide
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Closed Consultations Popup */}
        {showClosedPopup && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg shadow-xl w-[95vw] max-w-7xl my-4 flex flex-col max-h-[90vh] min-w-[300px] mx-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Pacienți cu consultații încheiate
                  </h3>
                  <button
                    onClick={() => setShowClosedPopup(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Caută în pacienți..."
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={closedSearchQuery}
                    onChange={(e) => setClosedSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto min-h-0">
                <div className="p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200 table-auto">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Prenume
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nume
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            CNP
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Data Consultației
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Notițe
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Acțiuni
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {closedConsultations
                          .filter(
                            (patient) =>
                              `${patient.prenume} ${patient.nume}`
                                .toLowerCase()
                                .includes(closedSearchQuery.toLowerCase()) ||
                              patient.email
                                ?.toLowerCase()
                                .includes(closedSearchQuery.toLowerCase()) ||
                              patient.cnp?.includes(closedSearchQuery)
                          )
                          .map((patient) => (
                            <tr 
                              key={patient.id}
                              className=""
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {patient.prenume}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {patient.nume}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {patient.email}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {patient.cnp}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {patient.planned_date
                                  ? formatDateTime(patient.planned_date)
                                  : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <button
                                  onClick={() => openNotesModal(patient)}
                                  className={`flex items-center gap-1 px-3 py-1 rounded-md transition-colors ${
                                    patient.notite && patient.notite.trim() !== ''
                                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  <Edit className="w-4 h-4" />
                                  Notițe
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/doctor/patient/${patient.id}/documents`
                                    )
                                  }
                                  className="text-[#007FFF] hover:text-[#024994] transition-colors"
                                >
                                  Vezi documentele
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 flex-shrink-0">
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowClosedPopup(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Închide
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notes Modal */}
      <AnimatePresence>
        {showNotesModal && selectedPatientForNotes && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Notițe pentru {selectedPatientForNotes.prenume} {selectedPatientForNotes.nume}
                  </h3>
                  <button
                    onClick={() => setShowNotesModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-4">
                <textarea
                  className="w-full h-64 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  value={currentNote}
                  onChange={(e) => setCurrentNote(e.target.value)}
                  placeholder="Introduceți notițe despre pacient..."
                />
              </div>

              <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  onClick={() => setShowNotesModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Anulează
                </button>
                <button
                  onClick={() => saveNotes(selectedPatientForNotes.id, currentNote)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  Salvează
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showUpload && (
        <DocumentUpload
          onClose={() => {
            setShowUpload(false);
          }}
          patientCnp={patient?.cnp}
        />
      )}

      <AnimatePresence>
        {paymentNotification && (
          <motion.div
            className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg ${
              paymentNotification.type === 'error'
                ? 'bg-red-100 text-red-800'
                : 'bg-green-100 text-green-800'
            }`}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            {paymentNotification.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
